import { CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { AuthService } from "../../core/services/auth.service";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { RouterLink } from "@angular/router";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiLink,
  TuiLoader,
  tuiLoaderOptionsProvider,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
  TuiTitle,
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiChevron,
  TuiDataListDropdownManager,
  TuiDataListWrapper,
  TuiFade,
  TuiFilterByInputPipe,
  TuiItemsWithMore,
  TuiPagination,
  TuiStatus,
  TuiStringifyContentPipe,
  TuiSwitch,
  TuiTabs,
} from "@taiga-ui/kit";
import { TuiCell, TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import { IAdmin } from "../../model/interface/user";
import { AdminService } from "../../core/services/admin.service";
import {
  TuiComboBoxModule,
  TuiInputSliderModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { ResetPasswordComponent } from "../../auth/reset-password/reset-password.component";
import { ForgotResetPasswordService } from "../../core/services/forgot-reset-password.service.service";
import { DisableUnlessPermissionDirective } from "../../core/directives/disable-unless-permission.directive";
import { loadListState, saveListState } from "../../shared/utils/list-state";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
@Component({
  selector: "app-user",
  imports: [
    DisableUnlessPermissionDirective,
    FormsModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiDataListWrapper,
    TuiBreadcrumbs,
    TuiButton,
    TuiDataList,
    TuiPagination,
    TuiDropdown,
    TuiFade,
    TuiTable,
    TuiLink,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiCell,
    TuiStatus,
    TuiItemsWithMore,
    TuiSearch,
    ReactiveFormsModule,
    TuiSwitch,
    CommonModule,
    TuiInputSliderModule,
    TuiTextfieldControllerModule,
    RouterLink,
    TuiComboBoxModule,
    TuiStringifyContentPipe,
    TuiLoader,
    TuiDataListDropdownManager,
  ],
  templateUrl: "./user.component.html",
  styleUrl: "./user.component.scss",
  providers: [DatePipe, tuiLoaderOptionsProvider({ size: "xl" })],
})
export class UserComponent implements OnInit {
  protected readonly breadcrumbs = ["Royal Hospital", "Users"];
  protected readonly sizes = ["m"] as const;
  protected open = false;
  protected openDropdownsize: TuiSizeL | TuiSizeS = "s";
  protected openDropdown = false;
  protected size = this.sizes[0];
  isLoading = false;
  userData: any;
  paginatedData: IAdmin[] = [];
  userService = inject(AdminService);
  showResults: string = "";
  filters = {
    searchText: "",
    status: null,
    archived: false,
  };
  pageIndex = 1;
  pageSize = 10;
  total: number = 6;
  sortField = "createdAt";
  sortOrder: number = -1;
  openMap: { [userId: string]: boolean } = {};
  loggedInUserCanEdit = false;
  /** Multi-select bulk-action state. Keyed by user _id. */
  selectedIds = new Set<string>();
  bulkBusy = false;
  private authService = inject(AuthService);

  constructor(
    private datePipe: DatePipe,
    private resetPasswordService: ForgotResetPasswordService,
    private alerts: TuiAlertService
  ) { }

  // Form setup
  protected readonly form = new FormGroup({
    search: new FormControl(""),
    select: new FormControl(""),
  });

  protected value = null;

  protected readonly items = [
    { status: "Active", value: true },
    { status: "Inactive", value: false },
  ];

  protected stringify(item: { status: string; value: boolean } | null): string {
    if (!item) return "";
    return item.status;
  }

  private readonly listStateKey = "user-list";

  ngOnInit(): void {
    this.restoreListState();
    this.loadUsers();
    this.checkUserPermission();
  }

  private restoreListState(): void {
    const restored = loadListState(this.listStateKey, {
      filters: this.filters,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    });
    this.filters = { ...this.filters, ...restored.filters };
    this.filters.searchText = ""
    this.pageIndex = restored.pageIndex;
    this.pageSize = restored.pageSize;
    this.sortField = restored.sortField;
    this.sortOrder = restored.sortOrder;
  }

  private rememberListState(): void {
    saveListState(this.listStateKey, {
      filters: this.filters,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    });
  }

  loadUsers(): void {
    // this.rememberListState();
    this.selectedIds = new Set<string>();
    const payload = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      filters: {
        ...this.filters,
        status: this.filters.status ? (this.filters.status as { value: boolean }).value : null,
      },
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    };

    this.isLoading = true;

    this.userService.getPagedUsers(payload).subscribe({
      next: (res: any) => {
        if (res && res.length > 0 && res[0].data) {
          this.userData = res[0].data;
          this.total = res[0].metadata[0].total;
        } else {
          this.userData = [];
          this.total = 0;
        }
      },
      error: (error) => {
        console.error("Error fetching user data:", error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  toggleDropdown(userId: string): void {
    this.openMap[userId] = !this.openMap[userId];
  }

  sort(): void {
    this.sortOrder = this.sortOrder === 1 ? -1 : 1;
    this.loadUsers();
  }

  applyFilters(newFilters: any): void {
    this.filters = { ...this.filters, ...newFilters };
    this.loadUsers();
  }


  applySort(): void {
    this.loadUsers();
  }

  onEnter(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.applyFilters({ searchText: this.filters.searchText });
    }
  }

  onPageChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.loadUsers();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = Number(pageSize);
    this.pageIndex = 1;
    this.loadUsers();
  }

  clearSearch(): void {
    this.filters.searchText = "";
    this.filters.status = null;
    this.applyFilters({ searchText: this.filters.searchText, status: null });
  }

  getPageCount(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  //Dropdown functinalities
  resetUserPassword(email: any): void {
    const payload = {
      email: email,
    };
    this.resetPasswordService.requestResetPassword(payload).subscribe({
      next: (response) => {
        this.alerts
          .open("Password reset information sent to user's email", {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
      },
      error: (error) => {
        console.error("Error resetting password:", error);
      },
    });
  }

  toggleActive(userId: string): void {
    this.isLoading = true;
    this.userService.getOneUserById(userId).subscribe((user: any) => {
      if (!user) return;
      const updatedUser = { ...user, active: !user.active };

      this.userService.updateUser(updatedUser).subscribe({
        next: (response) => {
          this.userData = response;
          const status = updatedUser.active ? "actived" : "deactivated";
          this.alerts
            .open(`User has been ${status} successfully.`, {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
          this.loadUsers();
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error("Error updating user data:", error);
          this.alerts
            .open("Failed to update user data.", {
              label: "Error",
              appearance: "error",
            })
            .subscribe();
        },
      });
    });
  }

  toggleArchive(userId: string): void {
    this.isLoading = true;
    this.userService.getOneUserById(userId).subscribe((user: any) => {
      if (!user) return;
      const updatedUser = { ...user, archived: !user.archived };

      this.userService.updateUser(updatedUser).subscribe({
        next: (response) => {
          // this.userData = response;
          const status = updatedUser.archived ? "archived" : "unarchived";
          this.alerts
            .open(`User has been ${status} successfully.`, {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
          this.loadUsers();
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error("Error updating user data:", error);
          this.alerts
            .open("Failed to update user data.", {
              label: "Error",
              appearance: "error",
            })
            .subscribe();
        },
      });
    });
  }

  /* ---------- Multi-select bulk actions ---------- */

  isSelected(userId: string): boolean {
    return this.selectedIds.has(userId);
  }

  toggleSelected(userId: string, checked: boolean): void {
    if (checked) this.selectedIds.add(userId);
    else this.selectedIds.delete(userId);
  }

  isAllSelected(): boolean {
    if (!this.userData || this.userData.length === 0) return false;
    return this.userData.every((u: any) => this.selectedIds.has(u._id));
  }

  toggleSelectAll(checked: boolean): void {
    if (!this.userData) return;
    if (checked) {
      this.userData.forEach((u: any) => this.selectedIds.add(u._id));
    } else {
      this.userData.forEach((u: any) => this.selectedIds.delete(u._id));
    }
  }

  selectionCount(): number {
    return this.selectedIds.size;
  }

  bulkArchiveSelected(archive: boolean): void {
    if (this.selectedIds.size === 0 || this.bulkBusy) return;
    const verb = archive ? "archive" : "unarchive";
    if (!confirm(`Are you sure you want to ${verb} ${this.selectedIds.size} user(s)?`)) return;
    this.bulkBusy = true;
    const ids = Array.from(this.selectedIds);
    const ops = ids.map((id) =>
      this.userService.getOneUserById(id).pipe(catchError(() => of(null))),
    );
    forkJoin(ops).subscribe({
      next: (users: any[]) => {
        const updates = users
          .filter((u: any) => !!u)
          .map((u: any) =>
            this.userService
              .updateUser({ ...u, archived: archive })
              .pipe(catchError(() => of(null))),
          );
        if (updates.length === 0) {
          this.bulkBusy = false;
          return;
        }
        forkJoin(updates).subscribe({
          next: () => {
            this.alerts
              .open(`${updates.length} user(s) ${archive ? "archived" : "unarchived"}.`, {
                label: "Success",
                appearance: "success",
              })
              .subscribe();
            this.bulkBusy = false;
            this.loadUsers();
          },
          error: () => {
            this.bulkBusy = false;
            this.alerts
              .open(`Bulk ${verb} failed for some rows.`, {
                label: "Error",
                appearance: "error",
              })
              .subscribe();
          },
        });
      },
      error: () => {
        this.bulkBusy = false;
      },
    });
  }

  exportSelectedToCsv(): void {
    if (!this.userData || this.userData.length === 0) return;
    const rows: any[] =
      this.selectedIds.size > 0
        ? this.userData.filter((u: any) => this.selectedIds.has(u._id))
        : this.userData;
    if (rows.length === 0) return;

    const headers = [
      "User ID",
      "User Name",
      "Email",
      "Phone Number",
      "Role",
      "Status",
      "Created Date",
    ];
    const escape = (val: any): string => {
      const s = (val ?? "").toString().replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [headers.join(",")];
    for (const r of rows) {
      lines.push(
        [
          r.refNo || "",
          r.name || "",
          r.email || "",
          r.contactNo || "",
          r.role?.name || "",
          r.active ? "Active" : "Inactive",
          this.datePipe.transform(r.createdAt, "yyyy-MM-dd") || "",
        ]
          .map(escape)
          .join(","),
      );
    }
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `users-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
  checkUserPermission(): void {
    this.loggedInUserCanEdit = this.authService.hasPermission("USER_UPDATE");
  }
}
